import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-02 at v1.973 (venue slice 1, part B2): what a job's or quote's venue says about getting
// in, the dock booking that answers the first warning, and the jobs whose dock is still to book. Keyed
// under the job or quote, so whatever refreshes them — a venue changed, a time moved — refreshes these.

export type VenueWarning = components['schemas']['VenueWarningView'];
export type VenueWarnings = components['schemas']['VenueWarningsView'];
export type JobVenueChecks = components['schemas']['JobVenueChecksView'];
export type DockToBook = components['schemas']['DockToBookView'];

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const jobVenueWarningsQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'venue-warnings'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/venue-warnings', {
        params: { path: { id: jobId } },
      });
      if (error) throw problem(error, 'Could not check the venue.');
      return data;
    },
  });

export const quoteVenueWarningsQuery = (quoteId: string) =>
  queryOptions({
    queryKey: ['quotes', quoteId, 'venue-warnings'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/quotes/{id}/venue-warnings', {
        params: { path: { id: quoteId } },
      });
      if (error) throw problem(error, 'Could not check the venue.');
      return data;
    },
  });

export const docksToBookQuery = queryOptions({
  queryKey: ['jobs', 'docks-to-book'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/venue-warnings/docks-to-book');
    if (error) throw problem(error, 'Could not list the docks to book.');
    return data;
  },
});

export async function bookDock(jobId: string, reference: string | null) {
  const { data, error } = await api.PUT('/v1/jobs/{id}/dock-booking', {
    params: { path: { id: jobId } },
    body: { reference },
  });
  if (error) throw problem(error, 'Could not record the dock booking.');
  return data;
}

export async function clearDockBooking(jobId: string) {
  const { error } = await api.DELETE('/v1/jobs/{id}/dock-booking', {
    params: { path: { id: jobId } },
  });
  if (error) throw problem(error, 'Could not clear the dock booking.');
}
